// What the public store API returns, and the calls the storefront makes. Prices shown here are information only:
// the server prices every cart again for the chosen Minecraft account, and checkout checks everything once more before it charges.
import { api } from "../../api";

export type Currency = "gems" | "orbs";
export type StoreAccount = { uuid: string; name: string; platform: "java" | "bedrock"; primary: boolean };

export type StoreCategory = { id: number; parentId: number | null; slug: string; name: string; description: string; icon: string | null; sortOrder: number; itemCount: number };
export type StoreOffer = {
  currency: Currency; unitPrice: number; listPrice: number; saleActive: boolean; saleEndsAt: string | null;
  upgradeFrom: { itemId: number; name: string } | null; available: boolean; reasons: string[]; relatedNames: string[]; reasonText: string;
};
export type StoreItem = {
  id: number; categoryId: number; slug: string; name: string; icon: string | null; imageUrl: string | null; descriptionMd: string; type: string; sortOrder: number;
  offers: StoreOffer[]; remainingStock: number | null; perAccountLimit: number | null; rank: { ladder: string; position: number; of: number } | null;
};
/** Which currencies the store takes right now (the owner's switches). */
export type Currencies = Record<Currency, boolean>;
export type StoreCatalog = {
  categories: StoreCategory[]; items: StoreItem[]; generatedAt: string; currencies: Currencies;
  account: { signedIn: boolean; accounts: StoreAccount[]; selected: StoreAccount | null };
};

export type CartLine = {
  itemId: number; slug: string | null; name: string; type: string | null; icon: string | null; currency: Currency; quantity: number;
  unitPrice: number; listPrice: number; total: number; saleActive: boolean; upgradeFrom: { itemId: number; name: string } | null;
  available: boolean; redundant: boolean; reasons: string[]; reasonText: string; maxQuantity: number;
};
export type Cart = { account: StoreAccount | null; currencies?: Currencies; lines: CartLine[]; totals: Record<Currency, number>; conflicts: Array<{ code: string; names: string[] }>; ok: boolean; count: number; note: string };

export type Blocker = { code: string; message: string; itemId?: number };
export type PreviewLine = CartLine & { included: boolean; payable: boolean; acceptsOrbs: boolean };
export type CheckoutPreview = {
  ok: boolean; blockers: Blocker[]; lines: PreviewLine[]; totals: Record<Currency, number>; note: string; shortfall: number; gemsShortfall?: number;
  /** `gems` is only there when the order costs Gems: the buyer's balance, or null when the server could not say. */
  balance: { orbs: number; gems?: number | null };
  account: { uuid: string; name: string; platform: "java" | "bedrock" | null } | null; recipient: { uuid: string; name: string; isGift: boolean } | null;
};

export type OrderStatus = "paid" | "queued" | "delivering" | "delivered" | "failed";
export type OrderLine = { itemId: number; name: string; currency: Currency; unitPrice: number; quantity: number; total: number; upgradeFrom: string | null };
export type Order = {
  id: string; status: OrderStatus; createdAt: string; deliveredAt: string | null; isGift: boolean; recipientName: string; buyerName: string | null;
  gemsTotal: number; orbsTotal: number; waitingForServer: boolean; lines: OrderLine[];
};
export type OrderPage = { items: Order[]; nextCursor: number | null };

export type LedgerEntry = { id: number; delta: number; reason: "giftcard_redeem" | "order" | "admin_adjust"; orderId: string | null; createdAt: string };
export type LedgerPage = { items: LedgerEntry[]; nextCursor: number | null };

/** What redeeming a gift card did. Orbs are in the wallet at once; Gems are given by the server, which may be a moment behind (or stuck). */
export type Redeemed =
  | { ok: true; currency: "orbs"; amount: number; orbs: number; balance: number }
  | { ok: true; currency: "gems"; amount: number; account: { name: string }; delivery: "credited" | "pending" | "failed"; delivered: boolean };

const BASE = "/api/v1/store";
const json = (body: unknown) => JSON.stringify(body);

export const storeApi = {
  catalog: () => api<StoreCatalog>(`${BASE}/catalog`),
  cart: () => api<Cart>(`${BASE}/cart`),
  accounts: () => api<{ accounts: StoreAccount[]; selectedUuid: string | null }>(`${BASE}/accounts`),
  select: (uuid: string) => api<{ account: StoreAccount; accounts: StoreAccount[] }>(`${BASE}/accounts/select`, { method: "POST", body: json({ uuid }) }),
  add: (itemId: number, currency: Currency, quantity: number) => api<Cart>(`${BASE}/cart/lines`, { method: "POST", body: json({ itemId, currency, quantity }) }),
  setQuantity: (itemId: number, currency: Currency, quantity: number) => api<Cart>(`${BASE}/cart/lines`, { method: "PATCH", body: json({ itemId, currency, quantity }) }),
  remove: (itemId: number, currency: Currency) => api<Cart>(`${BASE}/cart/lines?itemId=${itemId}&currency=${currency}`, { method: "DELETE" }),
  clear: () => api<Cart>(`${BASE}/cart`, { method: "DELETE" }),
  preview: (gift: string | null) => api<CheckoutPreview>(`${BASE}/checkout/preview`, { method: "POST", body: json(gift ? { gift: { name: gift } } : {}) }),
  // `expected` is the pair of totals the buyer was shown; the server charges nothing if the cart costs something else by now.
  confirm: (gift: string | null, idempotencyKey: string, expected?: { gems: number; orbs: number }) => api<{ order: Order; replayed: boolean }>(`${BASE}/checkout/confirm`, { method: "POST", body: json({ ...(gift ? { gift: { name: gift } } : {}), idempotencyKey, ...(expected ? { expected } : {}) }) }),
  orders: (before?: number | null) => api<OrderPage>(`${BASE}/orders${before ? `?before=${before}` : ""}`),
  order: (id: string) => api<{ order: Order }>(`${BASE}/orders/${encodeURIComponent(id)}`),
  wallet: () => api<{ balance: number; currencies: Currencies; ledger: LedgerPage }>(`${BASE}/wallet`),
  ledger: (before: number) => api<LedgerPage>(`${BASE}/wallet/ledger?before=${before}`),
  redeem: (code: string) => api<Redeemed>(`${BASE}/wallet/redeem`, { method: "POST", body: json({ code }) }),
};

export const CURRENCY_NAME: Record<Currency, string> = { gems: "Gems", orbs: "Orbs" };
export const priceText = (amount: number, currency: Currency) => `${new Intl.NumberFormat().format(amount)} ${CURRENCY_NAME[currency]}`;
export const platformLabel = (account: Pick<StoreAccount, "platform">) => (account.platform === "bedrock" ? "Bedrock" : "Java");
